import type { ReactNode } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import type { SvgIconSize } from 'LuxKit/SvgIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Group from 'components/utils/Group'
import SolidCarIcon from 'LuxKit/Icons/solid/SolidCarIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineSubwayIcon from 'LuxKit/Icons/line/LineSubwayIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import type { TransportOption } from 'tripPlanner/api/types'

export function getTransportIcon(type: string, size: SvgIconSize = 'M'): ReactNode {
  const normalised = type.toLowerCase().replace(/[_\s]/g, '')
  switch (normalised) {
    case 'flight':
    case 'leflight':
    case 'chatflight':
      return <LinePlaneDepartureIcon size={size} />
    case 'driving':
    case 'drive':
    case 'car':
    case 'carrental':
      return <SolidCarIcon size={size} />
    case 'publictransport':
    case 'transit':
    case 'bus':
      return <LineBusIcon size={size} />
    case 'train':
      return <LineSubwayIcon size={size} />
    case 'ferry':
    case 'cruise':
    case 'lecruise':
      return <LineShipIcon size={size} />
    default:
      return <LineCarIcon size={size} />
  }
}

interface Props {
  option: TransportOption
  isSelected: boolean
  onOptionClick: (itemId: number) => void
}

function TransportOptionItem({ option, isSelected, onOptionClick }: Props) {
  const intl = useIntl()
  const handleClick = useCallback(() => {
    onOptionClick(option.itemId)
  }, [onOptionClick, option.itemId])

  const label = option.label ?? option.type
  const title = (
    <Group direction="horizontal" horizontalAlign="space-between">
      <span>{label}</span>
      {option.duration && <span>{intl.formatMessage(
        {
          id: 'ORB6df4h0QiBVb9JPC9f',
          defaultMessage: '~{duration}',
          description: 'Approximate duration label shown for a transport option in the trip planner',
        },
        { duration: option.duration },
      )}
      </span>}
    </Group>
  )

  return (
    <ClickableListItem
      title={title}
      startIcon={getTransportIcon(option.type, 'S')}
      onClick={handleClick}
      selected={isSelected}
    />
  )
}

export default TransportOptionItem
